/* global axios */
// appointmentsV2.js
import { isOrganizationAccount } from '../../helper/organizationAccessHelper';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  appointment: null,
  appointmentList: [],
  isLoading: false,
  isError: false,

  // Filters
  filters: {
    status: null,
    customer_id: null,
    branch_id: null,
    staff_id: null,
    service_id: null,
    conversation_id: null,
    label: null,
    start_date: null,
    end_date: null,
  },

  // Pagination
  pagination: {
    page: 1,
    page_size: 10,
    total: 0,
    next: null,
  },

  // Sorting
  sort: {
    sort_by: 'date',
    sort_order: 'asc',
  },

  statusCounts: {},
};

const getters = {
  appointment: state => state.appointment,
  appointmentList: state => state.appointmentList,
  filters: state => state.filters,
  pagination: state => state.pagination,
  sort: state => state.sort,
  isLoading: state => state.isLoading,
  isError: state => state.isError,
  statusCounts: state => state.statusCounts,
};

const mutations = {
  SET_APPOINTMENT(state, payload) {
    state.appointment = payload;
  },
  CLEAR_APPOINTMENT(state) {
    state.appointment = null;
  },
  SET_APPOINTMENT_LIST(state, list) {
    state.appointmentList = list;
  },
  SET_IS_LOADING(state, value) {
    state.isLoading = value;
  },
  SET_IS_ERROR(state, value) {
    state.isError = value;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  CLEAR_FILTERS(state, defaultStatus = 'upcoming') {
    state.filters = {
      status: defaultStatus,
      customer_id: null,
      branch_id: null,
      staff_id: null,
      service_id: null,
      conversation_id: null,
      label: null,
      start_date: null,
      end_date: null,
    };
  },
  SET_SORT(state, sort) {
    state.sort = { ...state.sort, ...sort };
  },
  SET_STATUS_COUNTS(state, counts) {
    state.statusCounts = counts;
  },
};

const actions = {
  async fetchAppointments({ state, commit }, accountId) {
    commit('SET_IS_LOADING', true);
    commit('SET_IS_ERROR', false);

    try {
      const params = {
        ...state.filters,
        ...state.sort,
        page: state.pagination.page,
        page_size: state.pagination.page_size,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === '') delete params[key];
      });

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments`),
        { params }
      );

      const data = res.data;

      commit('SET_APPOINTMENT_LIST', data.results || []);
      commit('SET_STATUS_COUNTS', data.counts || {});
      commit('SET_PAGINATION', {
        total: data.total || 0,
        next: data.next || null,
      });

      return data;
    } catch (err) {
      console.error('fetchAppointments error:', err);
      commit('SET_IS_ERROR', true);
      throw err.response?.data || err;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async updateFiltersAndFetch({ commit, dispatch }, { accountId, filters }) {
    commit('SET_FILTERS', filters);
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchAppointments', accountId);
  },

  async clearFiltersAndFetch({ commit, dispatch }, accountId) {
    commit('CLEAR_FILTERS', 'upcoming'); // Default to 'upcoming' status
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchAppointments', accountId);
  },

  async updatePaginationAndFetch({ commit, dispatch, state }, { accountId, page, page_size }) {
    commit('SET_PAGINATION', { ...state.pagination, page, page_size });
    return dispatch('fetchAppointments', accountId);
  },

  async updateSortAndFetch({ commit, dispatch }, { accountId, sort_by, sort_order }) {
    commit('SET_SORT', { sort_by, sort_order });
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchAppointments', accountId);
  },

  async createAppointment({ commit, rootGetters }, payload) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const path = isOrganizationAccount({ getters: rootGetters }, accountId)
        ? `/v2/accounts/${accountId}/appointments`
        : `/accounts/${accountId}/appointments`;
      const res = await axios.post(
        knowledgeProxy(accountId, path),
        payload
      );
      commit('SET_APPOINTMENT', res.data);
      return res.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  },

  async fetchAppointment({ commit, rootGetters }, appointmentId) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments/${appointmentId}`)
      );
      commit('SET_APPOINTMENT', res.data);
      return res.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  },

  async deleteAppointment({ commit }, { accountId, appointmentId }) {
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments/${appointmentId}`)
      );
      commit('CLEAR_APPOINTMENT');
      return res.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};